import React, { useState } from 'react';
import './FAQ.scss';

const FAQS = [
  {
    id: 1,
    category: '🚨 Emergency',
    question: 'I accidentally put petrol in my diesel car — what should I do?',
    answer: `This is one of the most common and serious fuelling mistakes. Here's what to do:

**Do NOT start or turn on the engine** — this is the most important step. Running the engine circulates the contaminated fuel through the injection system, dramatically increasing repair costs (potentially £3,000–£10,000+).

**If you haven't started the engine:**
1. Tell the forecourt staff immediately
2. Put the car in neutral and push it away from the pump (or ask for help)
3. Call your breakdown provider (AA, RAC, etc.) — they have specialist "wrong fuel" units
4. The tank will need to be drained and flushed, which costs £150–£300

**If you've already started or driven the car:**
- Turn off the engine immediately
- Do not restart it
- Call breakdown assistance — recovery will be needed
- Repair costs increase significantly once the engine has run on contaminated fuel

**Why it's dangerous:** Petrol acts as a solvent in a diesel system — it strips lubrication from the high-pressure fuel pump, causing metal shavings that contaminate the whole system.`,
  },
  {
    id: 2,
    category: '🚨 Emergency',
    question: 'I put diesel in a petrol car — is it as serious?',
    answer: `Diesel in a petrol car is actually less catastrophic than the reverse, but still serious.

**Good news:** Diesel nozzles are wider than petrol filler necks, so this mistake is harder to make. If it did happen:

**Do NOT start the engine.** The diesel will foul your spark plugs and catalytic converter if circulated.

**What happens if driven:** The car will likely splutter, produce black smoke, and eventually stall. The catalytic converter is the main component at risk (replacement cost: £500–£2,000+).

**Action steps:**
1. Don't turn the key
2. Call breakdown assistance
3. Have the tank drained and flushed — typically £100–£250
4. The damage is usually limited if caught before starting

If you've only added a small amount of diesel (less than 5%) and filled the rest with petrol, some modern petrol cars may run it through without serious damage — but you should still have it checked by a mechanic.`,
  },
  {
    id: 3,
    category: '⛽ Fuel Types',
    question: 'What is the difference between E10 and E5 petrol? Which should I use?',
    answer: `Both are unleaded petrol, but they differ in ethanol content and octane rating:

**E10 (standard petrol):** Contains up to 10% bioethanol. It's cheaper, greener, and suitable for all petrol cars manufactured from 2011 onwards. Most modern engines are designed specifically for E10.

**E5 (Super Unleaded):** Contains no more than 5% ethanol and has a higher octane rating (typically 97–99 RON vs 95 RON for E10). It's better for:
- Performance and sports cars that demand high-octane fuel
- Older or classic cars (pre-2002 especially) where E10 can degrade rubber seals and fuel lines
- Vehicles explicitly stating "super unleaded" on the filler cap

**Which should you use?** Check your filler cap or handbook. If your car says "unleaded only" and was made after 2011, E10 is fine. If it says "super unleaded" or was made before 2002, use E5.

You can check E10 compatibility at: gov.uk/check-vehicle-e10-petrol-compatibility`,
  },
  {
    id: 4,
    category: '⛽ Fuel Types',
    question: 'Is premium / super diesel worth the extra money?',
    answer: `Super Diesel (SDV) typically costs 15–20p per litre more than standard B7 diesel. Whether it's worth it depends on your vehicle and mileage.

**The case for premium diesel:**
- Contains enhanced detergent additives that clean fuel injectors over time
- Can restore up to 5% fuel economy in older/high-mileage diesel engines with dirty injectors
- Reduces long-term maintenance costs by preventing carbon build-up
- Recommended by many manufacturers for turbocharged diesel engines

**The case against:**
- On a clean, modern diesel (under 30,000 miles), the benefit is negligible
- The fuel economy improvement rarely offsets the higher pump price in the short term
- Not worth it for short urban journeys where injector deposits aren't a major issue

**Verdict:** If you cover 20,000+ miles per year, have a high-performance diesel, or your injectors haven't been cleaned recently, a tank of premium diesel every 4–6 tanks is a worthwhile investment. For average drivers, it's optional.`,
  },
  {
    id: 5,
    category: '💰 Saving Money',
    question: 'When is the cheapest time to buy fuel in the UK?',
    answer: `Fuel prices at UK forecourts follow predictable weekly patterns based on how stations manage margins:

**Best days to fill up: Monday and Tuesday**
RAC and AA data consistently shows pump prices are statistically 0.5–1.2p/L lower on Monday and Tuesday compared to weekends. Stations tend to raise prices heading into the weekend when demand peaks.

**Worst days: Friday and Saturday**
Forecourts know drivers fill up before weekends — prices nudge upward to capture the demand.

**Best time of day:** Early morning (before 9am) — some stations update prices at the start of the day, and early fills catch pre-rise prices.

**Other tips:**
- Supermarket forecourts (Asda, Tesco, Sainsbury's, Morrisons) are consistently 4–7p/L cheaper than branded motorway stations
- Motorway services are the most expensive — avoid unless essential
- Use TTT or similar tools to compare prices before leaving — even 5p/L difference on a 50L fill saves £2.50

**Watch global events:** Crude oil prices spike on geopolitical news. Filling up before a major announcement (e.g. OPEC meetings) can save money.`,
  },
  {
    id: 6,
    category: '💰 Saving Money',
    question: 'Do loyalty cards and supermarket points actually save me money on fuel?',
    answer: `Yes — supermarket fuel loyalty schemes offer genuine savings, especially for frequent drivers.

**Tesco Clubcard:** Regularly offers 10p/L off fuel vouchers when you spend a certain amount in-store. Their forecourts already price 4–6p below premium brands, so combined savings can be 10–15p/L.

**Sainsbury's Nectar:** Earns Nectar points on fuel and occasionally offers bonus points or 10p/L vouchers for food shop combinations.

**Morrisons More:** Fuel saver stamps — collect with grocery purchases, redeem for p/L off at the pump.

**Asda:** Already prices as the UK's cheapest supermarket fuel. No complex scheme — just consistently low prices.

**BP, Shell, Esso loyalty apps:** Offer minor cashback (1–2%) or point systems. Less valuable than supermarket schemes but useful for regular motorway users.

**The honest verdict:** Supermarket points work best when you're already shopping there. Don't drive out of your way to use them — the extra mileage will cost more in fuel than you save. The biggest saving is simply choosing a supermarket forecourt over a branded forecourt to begin with.`,
  },
  {
    id: 7,
    category: '🔋 Electric & Alternative',
    question: 'Is it cheaper to run an electric car vs petrol or diesel in 2026?',
    answer: `Yes — significantly cheaper per mile, especially with home charging.

**Cost per mile comparison (2026 averages):**
- Petrol (E10 at 152p/L, 40mpg): ~17p per mile
- Diesel (B7 at 181p/L, 50mpg): ~16p per mile
- Electric (home charging at 24p/kWh, 3.5 miles/kWh): ~7p per mile
- Electric (public rapid charger at 75p/kWh): ~21p per mile

**The home charging advantage is enormous** — charging overnight on a standard tariff (or an EV-specific tariff like Octopus Go at ~8p/kWh off-peak) costs as little as 2–3p per mile.

**Annual saving for 10,000 miles:**
- Petrol → EV (home charging): saves ~£1,000/year in fuel
- Diesel → EV (home charging): saves ~£900/year in fuel

**Caveats:**
- Higher upfront purchase cost for EVs (typically £5,000–£15,000 more than equivalent petrol)
- Without home charging, rapid charging costs can exceed petrol
- Best for drivers doing regular, predictable journeys under 200 miles

**Bottom line:** If you have a driveway and charge at home, EVs are dramatically cheaper to run. The payback period on the purchase premium is typically 3–5 years for high-mileage drivers.`,
  },
  {
    id: 8,
    category: '🔋 Electric & Alternative',
    question: 'What is AdBlue and does my diesel car need it?',
    answer: `AdBlue is a diesel exhaust fluid (a solution of urea and deionised water) used in modern diesel vehicles fitted with Selective Catalytic Reduction (SCR) exhaust systems.

**Which cars need it?**
Most diesel cars manufactured after 2015 that meet Euro 6 emissions standards require AdBlue. This includes most modern diesel SUVs, MPVs, and many diesel cars from Volkswagen, BMW, Mercedes, Ford, Vauxhall, etc. Check your handbook or look for a blue filler cap near the fuel cap.

**How often does it need topping up?**
Typically every 5,000–10,000 miles, though this varies. Many cars have a dashboard warning light when levels are low — do not ignore it.

**What happens if it runs out?**
Modern Euro 6 diesel cars will refuse to start (or limit engine performance) if AdBlue runs empty. This is a legal requirement to prevent emissions cheating.

**Where to buy it?**
Most petrol stations sell AdBlue (often in 5L or 10L containers), as do Halfords and motor factors. Costs approximately £1.50–£2.50 per litre.

**Important:** Never put AdBlue in the diesel fuel tank. It will cause serious damage to fuel injection components. It goes in a separate dedicated tank, usually with a blue filler cap.`,
  },
  {
    id: 9,
    category: '📍 Finding Fuel',
    question: 'What should I do if I run out of fuel on the motorway?',
    answer: `Running out of fuel on the motorway is both dangerous and expensive. Here's exactly what to do:

**Immediate steps:**
1. Signal left and move to the hard shoulder (or emergency refuge area on smart motorways) as soon as the fuel warning light comes on — don't push your luck
2. Switch on your hazard lights immediately
3. Exit the vehicle from the left-hand door and stand behind the barrier — never stand between your car and live traffic
4. Call for breakdown assistance: AA (0800 887766), RAC (0330 2000999), or your insurer's rescue line

**Smart motorways (no hard shoulder):** Move to an emergency refuge area (ERA) marked by orange SOS phones and blue signs. These are spaced every 500–800m. If you stop in a live lane, call 999 immediately.

**What breakdown services will do:**
- Bring a small amount of fuel (typically 5–10 litres) to get you to the next services
- Some charge for the fuel separately

**Prevention tips:**
- Fill up before motorway journeys — services are expensive
- Don't let the tank drop below ¼ on long trips
- Use TTT to find cheap fuel just before joining the motorway

Running out of fuel on the motorway can result in a fine for causing unnecessary obstruction — prevention is always better.`,
  },
  {
    id: 10,
    category: '📍 Finding Fuel',
    question: 'Why are fuel prices so different between stations, even on the same street?',
    answer: `Fuel pricing in the UK is surprisingly complex, and the variation — sometimes 10–15p/L on the same road — comes down to several factors:

**1. Business model and ownership:** Supermarket forecourts (Asda, Tesco) use fuel as a "loss leader" to drive footfall into the store, so they price aggressively. Independent branded stations must cover higher costs.

**2. Motorway premium:** Services stations have a captive audience and very high site costs (rent, rates, staffing). This justifies charging 10–20p/L more than comparable off-motorway stations.

**3. Supply contracts and buying power:** Large chains buy fuel in bulk at better wholesale rates. Small independents pay more per litre at wholesale.

**4. Local competition:** A station next to a Tesco is under more pricing pressure than one in a rural area with no competition.

**5. Update frequency:** Under the Gov Fuel Finder Scheme (February 2026), stations must now update prices within 30 minutes of any change. Before this, prices could go unchanged for days despite wholesale cost movements.

**6. Brand positioning:** Premium brands (Shell V-Power, BP Ultimate) charge more as a positioning strategy, not just for cost.

**The new scheme helps:** The Government Fuel Finder Scheme now forces transparency — stations can no longer quietly maintain high prices without being visible on comparison tools like TTT.`,
  },
];

const CATEGORIES = ['All', ...new Set(FAQS.map((f) => f.category))];

export default function FAQ() {
  const [openId,       setOpenId]       = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');
  const [search,       setSearch]       = useState('');

  const toggle = (id) => setOpenId((prev) => (prev === id ? null : id));

  const filtered = FAQS.filter((faq) => {
    const matchCat    = activeCategory === 'All' || faq.category === activeCategory;
    const matchSearch = !search ||
      faq.question.toLowerCase().includes(search.toLowerCase()) ||
      faq.answer.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  // Render bold markdown-style text
  const renderAnswer = (text) => {
    return text.split('\n').map((line, i) => {
      if (!line.trim()) return <br key={i} />;
      const parts = line.split(/(\*\*.*?\*\*)/g).map((part, j) =>
        part.startsWith('**') && part.endsWith('**')
          ? <strong key={j}>{part.slice(2, -2)}</strong>
          : part
      );
      // Detect list items
      if (line.trim().startsWith('- ')) {
        return <li key={i}>{parts.map((p, j) => typeof p === 'string' ? p.replace(/^- /, '') : p)}</li>;
      }
      if (/^\d+\./.test(line.trim())) {
        return <li key={i}>{parts}</li>;
      }
      return <p key={i}>{parts}</p>;
    });
  };

  return (
    <section id="faq" className="faq">
      <div className="faq__inner">

        <div className="faq__header">
          <p className="faq__label">10 Questions · Always Growing</p>
          <h2 className="faq__title">Frequently Asked Questions</h2>
          <p className="faq__desc">
            Everything UK drivers need to know — from fuel types and misfuelling emergencies
            to saving money and what to do when things go wrong.
          </p>
        </div>

        {/* Search */}
        <div className="faq__search-row">
          <input
            className="faq__search"
            type="text"
            placeholder="Search questions… e.g. 'petrol in diesel'"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="faq__search-clear" onClick={() => setSearch('')}>✕</button>
          )}
        </div>

        {/* Category filter */}
        <div className="faq__categories">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              className={`faq__cat-btn${activeCategory === cat ? ' faq__cat-btn--active' : ''}`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* FAQ items */}
        <div className="faq__list">
          {filtered.length === 0 ? (
            <div className="faq__empty">No questions match your search. Try different keywords.</div>
          ) : (
            filtered.map((faq) => (
              <div
                key={faq.id}
                className={`faq-item${openId === faq.id ? ' faq-item--open' : ''}`}
              >
                <button className="faq-item__question" onClick={() => toggle(faq.id)}>
                  <span className="faq-item__category">{faq.category}</span>
                  <span className="faq-item__text">{faq.question}</span>
                  <span className="faq-item__icon">{openId === faq.id ? '−' : '+'}</span>
                </button>

                {openId === faq.id && (
                  <div className="faq-item__answer">
                    <div className="faq-item__answer-body">
                      {renderAnswer(faq.answer)}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Bottom CTA */}
        <div className="faq__bottom">
          <p>Still have a question? Speak to a qualified technician for help</p>
          <a href="#reg" className="faq__bottom-link">Check your car's fuel type →</a>
        </div>

      </div>
    </section>
  );
}
